// src/store/systemSettingsStore.ts
import { create } from 'zustand';
import { adminService } from '../services/admin';
import api from '../services/api';

export interface SystemSettings {
  app_name: string;
  app_description: string;
  institution_name: string;
  institution_code: string;
  institution_address: string;
  institution_phone: string;
  institution_email: string;
  institution_website: string;
  logo_url: string;
  favicon_url: string;
  login_background: string;
  footer_text: string;
  primary_color: string;
  // SLA
  sla_rendah: number;
  sla_sedang: number;
  sla_tinggi: number;
  sla_critical: number;
  // Password
  password_min_length: number;
  password_require_number: boolean;
  password_require_special: boolean;
  password_require_uppercase: boolean;
  [key: string]: any;
}

interface SystemSettingsState {
  settings: SystemSettings;
  isLoading: boolean;
  isHydrated: boolean;
  fetchSettings: () => Promise<void>;
  fetchPublicSettings: () => Promise<void>;
  updateSettings: (newSettings: Partial<SystemSettings>) => void;
}

const DEFAULT_SETTINGS: SystemSettings = {
  app_name: 'SIM-EVAL',
  app_description: 'Sistem Informasi Manajemen Evaluasi',
  institution_name: 'Universitas Buana Perjuangan Karawang',
  institution_code: 'UBP-KRW',
  institution_address: 'Jl. HS. Ronggowaluyo, Telukjambe Timur, Karawang 41361',
  institution_phone: '(0267) 8404010',
  institution_email: 'info@ubpkarawang.ac.id',
  institution_website: 'https://www.ubpkarawang.ac.id',
  logo_url: '',
  favicon_url: '',
  login_background: '',
  footer_text: '© 2026 SIM-EVAL UBP Karawang. All rights reserved.',
  primary_color: '#0f33ab',
  sla_rendah: 60,
  sla_sedang: 30,
  sla_tinggi: 14,
  sla_critical: 7,
  password_min_length: 8,
  password_require_number: false,
  password_require_special: false,
  password_require_uppercase: false,
};

// ✅ FIX: Helper function untuk parse boolean dengan benar
const parseBoolean = (value: any): boolean => {
  if (value === true || value === 'true' || value === '1' || value === 1) {
    return true;
  }
  // Semua nilai lain (termasuk '0', 'false', false, 0, undefined, null) = false
  return false;
};

// ✅ FIX: Helper untuk parse number
const parseNumber = (value: any, defaultVal: number): number => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultVal : parsed;
};

const getStoredSettings = (): SystemSettings => {
  try {
    const stored = localStorage.getItem('system_settings');
    if (stored) {
      const parsed = JSON.parse(stored);
      
      // ✅ FIX: Re-parse booleans saat load dari localStorage
      const booleanKeys = ['password_require_number', 'password_require_special', 'password_require_uppercase'];
      booleanKeys.forEach(key => {
        if (key in parsed) {
          parsed[key] = parseBoolean(parsed[key]);
        }
      });
      
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to parse stored settings:', e);
  }
  return DEFAULT_SETTINGS;
};

export const useSystemSettingsStore = create<SystemSettingsState>((set, get) => ({
  settings: getStoredSettings(),
  isLoading: false,
  isHydrated: !!localStorage.getItem('system_settings'),

  fetchSettings: async () => {
    if (get().isLoading) return;
    set({ isLoading: true });
    
    try {
      const res = await adminService.settings.getAll();
      const data = res || [];
      const newSettings: SystemSettings = { ...DEFAULT_SETTINGS };
      
      // ✅ FIX: Parse dengan benar berdasarkan type
      if (Array.isArray(data)) {
        data.forEach((item: any) => {
          if (item.type === 'boolean') {
            newSettings[item.key] = parseBoolean(item.value);
          } else if (item.type === 'number' || item.type === 'int') {
            newSettings[item.key] = parseNumber(item.value, 0);
          } else {
            newSettings[item.key] = item.value ?? '';
          }
        });
      }
      
      localStorage.setItem('system_settings', JSON.stringify(newSettings));
      set({ settings: newSettings, isLoading: false, isHydrated: true });
      
    } catch (e) {
      console.error('Failed to fetch settings:', e);
      set({ isLoading: false });
    }
  },

  fetchPublicSettings: async () => {
    if (get().isLoading) return;
    set({ isLoading: true });
    
    try {
      const response = await api.get('/public/settings');
      const res = response.data?.data || response.data || {};
      
      // ✅ FIX: Parse semua nilai dengan benar
      const updatedSettings = { ...get().settings };
      
      // Parse semua keys dari response
      Object.keys(res).forEach(key => {
        const value = res[key];
        
        // Password policy booleans
        if (['password_require_number', 'password_require_special', 'password_require_uppercase'].includes(key)) {
          updatedSettings[key] = parseBoolean(value);
        }
        // Numbers
        else if (['password_min_length', 'sla_rendah', 'sla_sedang', 'sla_tinggi', 'sla_critical'].includes(key)) {
          updatedSettings[key] = parseNumber(value, DEFAULT_SETTINGS[key as keyof SystemSettings] as number);
        }
        // Strings
        else {
          updatedSettings[key] = value ?? '';
        }
      });
      
      localStorage.setItem('system_settings', JSON.stringify(updatedSettings));
      set({ settings: updatedSettings, isLoading: false, isHydrated: true });
      
    } catch (e) {
      console.error('Failed to fetch public settings:', e);
      set({ isLoading: false });
    }
  },

  updateSettings: (newSettings) => {
    const current = get().settings;
    
    // ✅ FIX: Pastikan boolean di-parse saat update
    const parsed: Partial<SystemSettings> = {};
    Object.keys(newSettings).forEach(key => {
      const value = newSettings[key];
      if (['password_require_number', 'password_require_special', 'password_require_uppercase'].includes(key)) {
        parsed[key] = parseBoolean(value);
      } else {
        parsed[key] = value;
      }
    });
    
    const updated = { ...current, ...parsed };
    localStorage.setItem('system_settings', JSON.stringify(updated));
    set({ settings: updated, isHydrated: true });
  },
}));
